import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Button,
  Input,
  Form,
  Radio,
  Space,
  Typography,
  Tabs,
  Tag,
  Empty,
  Pagination,
} from 'antd';
import { useMutation } from '@apollo/client';
import moment from 'moment';
// Api
import {
  ATHLETE_HANDLE_COUNTER_OFFER,
  BRAND_HANDLE_COUNTER_OFFER,
} from 'api/campaignV2/counter-offer/mutations';
// Types
import { GetCampaignOffers_getCampaignOffers_entities } from 'api/campaignV2/types/GetCampaignOffers';
import {
  AthleteHandleCounterOffer,
  AthleteHandleCounterOfferVariables,
} from 'api/campaignV2/counter-offer/types/AthleteHandleCounterOffer';
import {
  BrandHandleCounterOffer,
  BrandHandleCounterOfferVariables,
} from 'api/campaignV2/counter-offer/types/BrandHandleCounterOffer';
import {
  CampaignOfferStatus,
  CounterOfferAction,
  CounterOfferProposedBy,
} from 'api/graphql-global-types';
// Helpers
import {
  isAthletesTurn,
  isBrandsTurn,
  getOriginalAmounts,
  groupCounterOffersIntoPairs,
} from 'helpers/counterOffer';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './CounterOfferModal.module.scss';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

type CounterOfferModalProps = {
  offer: GetCampaignOffers_getCampaignOffers_entities;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const CounterOfferModal = ({
  offer,
  visible,
  onClose,
  onSuccess,
}: CounterOfferModalProps): JSX.Element => {
  const [form] = Form.useForm();
  const [action, setAction] = useState<CounterOfferAction>(
    CounterOfferAction.COUNTER
  );
  const [activeTab, setActiveTab] = useState('action');

  const isInitialCounter = offer.statusV2 === CampaignOfferStatus.INVITED;
  const athletesTurn = isAthletesTurn(offer);
  const brandsTurn = isBrandsTurn(offer);

  // Determine which mutation to use
  const useAthleteMutation = isInitialCounter || athletesTurn;

  const [athleteHandleCounterOffer, { loading: athleteLoading }] = useMutation<
    AthleteHandleCounterOffer,
    AthleteHandleCounterOfferVariables
  >(ATHLETE_HANDLE_COUNTER_OFFER);

  const [brandHandleCounterOffer, { loading: brandLoading }] = useMutation<
    BrandHandleCounterOffer,
    BrandHandleCounterOfferVariables
  >(BRAND_HANDLE_COUNTER_OFFER);

  const loading = athleteLoading || brandLoading;

  // Get the latest counter offer for responding
  const latestCounterOffer = offer.counterOffers?.[0] || null;
  const hasCounterOffers =
    offer.counterOffers && offer.counterOffers.length > 0;

  // Fee calculations
  // Original fees from offer
  const { originalTalentFee, originalBrandFee } = getOriginalAmounts(offer);

  // Latest counter offer fees
  const latestTalentFee = latestCounterOffer?.proposedInfluencerFee;
  const latestBrandFee = latestCounterOffer?.proposedAmount;

  // Check if offer is accepted
  const isAccepted = offer.statusV2 === CampaignOfferStatus.ACCEPTED;

  // If accepted, the latest counter offer fees are the accepted fees
  const acceptedTalentFee = isAccepted ? latestTalentFee : null;
  const acceptedBrandFee = isAccepted ? latestBrandFee : null;

  // Check if actions can be taken (only in INVITED or COUNTER_OFFER_PENDING state)
  const canTakeAction =
    offer.statusV2 === CampaignOfferStatus.INVITED ||
    offer.statusV2 === CampaignOfferStatus.COUNTER_OFFER_PENDING;

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setAction(CounterOfferAction.COUNTER);
      // Default to history tab if no action can be taken
      setActiveTab(canTakeAction ? 'action' : 'history');
    }
  }, [visible, form, canTakeAction]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      const input = {
        offerId: offer.id,
        action,
        ...(action === CounterOfferAction.COUNTER && {
          amount: parseFloat(values.amount),
        }),
        ...(values.message && { message: values.message.trim() }),
        ...(latestCounterOffer && { counterProposalId: latestCounterOffer.id }),
      };

      if (useAthleteMutation) {
        await athleteHandleCounterOffer({
          variables: { input },
        });
      } else {
        await brandHandleCounterOffer({
          variables: { input },
        });
      }

      const actionLabel =
        action === CounterOfferAction.COUNTER
          ? 'sent'
          : action === CounterOfferAction.ACCEPT
          ? 'accepted'
          : 'rejected';

      successNotification(`Counter offer ${actionLabel} successfully!`);
      onSuccess();
      onClose();
    } catch (error) {
      errorNotification((error as Error)?.message || 'Something went wrong');
    }
  }, [
    form,
    offer.id,
    action,
    latestCounterOffer,
    useAthleteMutation,
    athleteHandleCounterOffer,
    brandHandleCounterOffer,
    onSuccess,
    onClose,
  ]);

  const getModalTitle = () => {
    if (!canTakeAction) {
      return 'Counter Offer History';
    }
    if (isInitialCounter) {
      return 'Initiate Counter Offer (as Athlete)';
    }
    if (athletesTurn) {
      return 'Respond to Counter Offer (as Athlete)';
    }
    if (brandsTurn) {
      return 'Respond to Counter Offer (as Brand)';
    }
    return 'Counter Offer';
  };

  const showActionSelector = !isInitialCounter;

  return (
    <Modal
      title={getModalTitle()}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div className={styles.root}>
        <div className={styles.info}>
          {offer.statusV2 === CampaignOfferStatus.DECLINED ? (
            <div className={styles.infoRow}>
              <Text type="secondary" className={styles.declinedTag}>
                DECLINED
              </Text>
            </div>
          ) : offer.statusV2 === CampaignOfferStatus.ACCEPTED ? (
            <div className={styles.infoRow}>
              <Text type="secondary" className={styles.acceptedTag}>
                ACCEPTED
              </Text>
            </div>
          ) : null}
          <div className={styles.infoRow}>
            <Text type="secondary">Talent:</Text>
            <Text strong>
              {offer.firstName} {offer.lastName}
            </Text>
          </div>
          <div className={styles.infoRow}>
            <Text type="secondary">Original Talent Fee:</Text>
            <Text strong>${originalTalentFee.toFixed(2)}</Text>
          </div>
          <div className={styles.infoRow}>
            <Text type="secondary">Original Brand Fee:</Text>
            <Text strong>${originalBrandFee.toFixed(2)}</Text>
          </div>

          {hasCounterOffers && (
            <div className={styles.feeSection}>
              <Text type="secondary" className={styles.feeSectionTitle}>
                {isAccepted
                  ? 'Accepted Fees'
                  : hasCounterOffers
                  ? 'Latest Counter Offer Fees'
                  : 'Original Fees'}
              </Text>
              <div className={styles.feeGrid}>
                <div className={styles.feeItem}>
                  <Text type="secondary">Talent Fee:</Text>
                  <Text strong className={styles.talentFee}>
                    $
                    {(isAccepted
                      ? acceptedTalentFee
                      : latestTalentFee
                    )?.toFixed(2)}
                  </Text>
                </div>
                <div className={styles.feeItem}>
                  <Text type="secondary">Brand Fee:</Text>
                  <Text strong className={styles.brandFee}>
                    $
                    {(isAccepted ? acceptedBrandFee : latestBrandFee)?.toFixed(
                      2
                    )}
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {canTakeAction && (
            <TabPane tab="Counter Offer Action" key="action">
              <Form form={form} layout="vertical" className={styles.form}>
                {showActionSelector && (
                  <Form.Item label="Action" required>
                    <Radio.Group
                      value={action}
                      onChange={(e) => setAction(e.target.value)}
                    >
                      <Space direction="vertical">
                        <Radio value={CounterOfferAction.COUNTER}>
                          Counter with new amount
                        </Radio>
                        <Radio value={CounterOfferAction.ACCEPT}>
                          Accept counter offer
                        </Radio>
                        <Radio value={CounterOfferAction.REJECT}>
                          Reject counter offer
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                )}

                {action === CounterOfferAction.COUNTER && (
                  <Form.Item
                    name="amount"
                    label="Counter Amount ($)"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a counter amount',
                      },
                      {
                        validator: (_, value) => {
                          const num = parseFloat(value);
                          if (isNaN(num) || num <= 0) {
                            return Promise.reject(
                              'Please enter a valid amount'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      placeholder="Enter counter offer amount"
                      prefix="$"
                      min={0}
                      step={0.01}
                    />
                  </Form.Item>
                )}

                {action === CounterOfferAction.COUNTER && (
                  <Form.Item name="message" label="Notes (Optional)">
                    <TextArea
                      rows={4}
                      placeholder="Add any additional notes or messages..."
                      maxLength={1500}
                      showCount
                    />
                  </Form.Item>
                )}

                <div className={styles.actions}>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading}
                    danger={action === CounterOfferAction.REJECT}
                  >
                    {action === CounterOfferAction.COUNTER && 'Submit Counter'}
                    {action === CounterOfferAction.ACCEPT && 'Accept Offer'}
                    {action === CounterOfferAction.REJECT && 'Reject Offer'}
                  </Button>
                </div>
              </Form>
            </TabPane>
          )}

          {hasCounterOffers && (
            <TabPane
              tab={`Counter Offer History (${
                groupCounterOffersIntoPairs(offer.counterOffers || []).length
              })`}
              key="history"
            >
              <CounterOfferHistoryTab offer={offer} />
            </TabPane>
          )}
        </Tabs>
      </div>
    </Modal>
  );
};

// Counter Offer History Tab Component
type CounterOfferHistoryTabProps = {
  offer: GetCampaignOffers_getCampaignOffers_entities;
};

const CounterOfferHistoryTab = ({
  offer,
}: CounterOfferHistoryTabProps): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 1;

  const counterOffers = offer.counterOffers || [];
  const pairs = groupCounterOffersIntoPairs(counterOffers);

  const currentPair = pairs[currentPage - 1];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getProposedByLabel = (
    counterOffer: NonNullable<
      GetCampaignOffers_getCampaignOffers_entities['counterOffers']
    >[number]
  ) => {
    const { proposedBy, proposedByUserId } = counterOffer;
    const isAdminAction =
      proposedByUserId !== offer.userId ||
      offer.campaign.agency.id !== proposedByUserId;

    switch (proposedBy) {
      case CounterOfferProposedBy.ATHLETE:
        return (
          <Space>
            <Tag color="blue">Athlete</Tag>
            {isAdminAction && <Tag color="orange">By Admin</Tag>}
          </Space>
        );
      case CounterOfferProposedBy.BRAND:
        return (
          <Space>
            <Tag color="green">Brand</Tag>
            {isAdminAction && <Tag color="orange">By Admin</Tag>}
          </Space>
        );
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  if (pairs.length === 0) {
    return <Empty description="No counter offers yet" />;
  }

  return (
    <div className={styles.historyTab}>
      {currentPair && (
        <div className={styles.pairContainer}>
          <Title level={5} className={styles.counterLabel}>
            Counter #{currentPair.counterNumber}
          </Title>

          <CounterOfferItem
            counterOffer={currentPair.counterOffer}
            getProposedByLabel={() =>
              getProposedByLabel(currentPair.counterOffer)
            }
          />

          {currentPair.response && (
            <>
              <div className={styles.divider}>
                <Text type="secondary">Response</Text>
              </div>
              <CounterOfferItem
                counterOffer={currentPair.response}
                getProposedByLabel={() =>
                  getProposedByLabel(
                    currentPair.response as NonNullable<
                      typeof currentPair.response
                    >
                  )
                }
              />
            </>
          )}
        </div>
      )}

      {pairs.length > 1 && (
        <div className={styles.pagination}>
          <Pagination
            current={currentPage}
            total={pairs.length}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            simple
          />
        </div>
      )}
    </div>
  );
};

type CounterOfferItemProps = {
  counterOffer: NonNullable<
    GetCampaignOffers_getCampaignOffers_entities['counterOffers']
  >[number];
  getProposedByLabel: () => JSX.Element;
};

const CounterOfferItem = ({
  counterOffer,
  getProposedByLabel,
}: CounterOfferItemProps): JSX.Element => {
  return (
    <div className={styles.counterItem}>
      <div className={styles.itemRow}>
        <Text type="secondary">Proposed By:</Text>
        {getProposedByLabel()}
      </div>
      <div className={styles.itemRow}>
        <Text type="secondary">Talent Fee:</Text>
        <Text strong className={styles.talentFee}>
          ${counterOffer.proposedInfluencerFee.toFixed(2)}
        </Text>
      </div>
      <div className={styles.itemRow}>
        <Text type="secondary">Brand Fee:</Text>
        <Text strong className={styles.brandFee}>
          ${counterOffer.proposedAmount.toFixed(2)}
        </Text>
      </div>
      <div className={styles.itemRow}>
        <Text type="secondary">Date:</Text>
        <Text>
          {moment(counterOffer.createdAt).format('MMM DD, YYYY h:mm A')}
        </Text>
      </div>
      {(counterOffer.originalMessage || counterOffer.message) && (
        <div className={styles.messageSection}>
          <Text type="secondary">Notes:</Text>
          <div className={styles.message}>
            <Text>{counterOffer.originalMessage || counterOffer.message}</Text>
          </div>
          {counterOffer.originalMessage &&
            counterOffer.originalMessage !== counterOffer.message && (
              <Text type="secondary" className={styles.messageHint}>
                Brand sees: {counterOffer.message || '(empty after filtering)'}
              </Text>
            )}
        </div>
      )}
    </div>
  );
};

export default CounterOfferModal;
